import { createContext, useContext, useEffect, useState } from 'react';
import type { Permission, PermissionKey, UserPermissions } from 'types/rbac';
import { getPermissions, storePermissions } from 'utils/rbac';

type PermissionsContextType = {
  permissions: UserPermissions | null;
  isLoading: boolean;
  fetchPermissions: () => Promise<void>;
  updatePermissions: (newPermissions: UserPermissions) => Promise<void>;
  checkPermissions: (
    permissionKey?: PermissionKey | PermissionKey[],
    checkAllPermissions?: boolean,
  ) => boolean;
  clearPermissions: () => Promise<void>;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined,
);

export const PermissionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = async () => {
    setIsLoading(true);
    const permissions = await getPermissions();
    setPermissions(permissions);
    setIsLoading(false);
  };

  const clearPermissions = async () => {
    localStorage.removeItem('userPermissions');
    setPermissions(null);
  };

  const updatePermissions = async (newPermissions: UserPermissions) => {
    await storePermissions(newPermissions);
    setPermissions(newPermissions);
  };

  const hasPermission = (permissionKey: PermissionKey) => {
    const [resource, action] = permissionKey.split(':') as [
      keyof UserPermissions,
      keyof Permission,
    ];
    return permissions?.[resource]?.[action] ?? false;
  };

  const checkPermissions = (
    requiredPermissions?: PermissionKey | PermissionKey[],
    checkAllPermissions: boolean = true,
  ) => {
    if (!permissions) return false;

    // if no permissions are required, return true
    if (!requiredPermissions) return true;
    if (typeof requiredPermissions === 'string') {
      return hasPermission(requiredPermissions);
    }

    // if no permissions are required, return true
    if (requiredPermissions.length === 0) return true;
    // check if all or any of the required permissions are granted
    if (!checkAllPermissions) {
      return requiredPermissions?.some((key) => hasPermission(key));
    }
    return requiredPermissions?.every((key) => hasPermission(key));
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isLoading,
        fetchPermissions,
        updatePermissions,
        checkPermissions,
        clearPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
